import { omit } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { Route, RouteChildrenProps } from 'react-router';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { CurrentUserFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { withErrorBoundary } from 'src/shared/errors/withErrorBoundary';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { withLoading } from 'src/shared/routing/withLoading';
import { LoadingScreenWithHeight } from 'web-common/src/shared/design-system/components/LoadingScreen';
import { RoutePropsOnlyComponent, withPageViewTracking } from './tracked-route';

interface TrackedAuthRouteWithErrorBoundaryProps extends RoutePropsOnlyComponent {
  loginHeader?: string;
}

export interface AuthenticatedRouteChildrenProps extends RouteChildrenProps {
  currentUser: CurrentUserFieldsFragment;
}

const AuthenticatedComponent: React.FC<{
  component: React.ComponentType<any>;
  loginHeader?: string;
  routeProps: RouteChildrenProps;
}> = ({ component: Component, loginHeader, routeProps }) => {
  const [currentUser, currentUserIsLoading] = useCurrentUser();
  const { show } = useShowLoginOrSignUpModal();
  const userId = useMemo(() => currentUser?.id, [currentUser?.id]);

  // Show login modal when user is not authenticated
  useEffect(() => {
    if (!userId) {
      show({
        signUpEntryPoint: 'auth_required',
        isClosable: false,
        entryHeader: loginHeader,
        preventReloadOnLogin: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // we only want to trigger this when the userId changes

  if (!userId) {
    const loadingMessage = currentUserIsLoading ? undefined : loginHeader ?? 'Please log in to continue';
    return <LoadingScreenWithHeight loadingMessage={loadingMessage} />;
  }

  return <Component {...routeProps} currentUser={currentUser} />;
};

export const TrackedAuthRouteWithErrorBoundary: React.FC<TrackedAuthRouteWithErrorBoundaryProps> = (props) => {
  const component = React.useMemo(
    () =>
      withPageViewTracking(withErrorBoundary(withLoading(props.component)), {
        suppressPageView: props.suppressPageView,
        beforePageView: props.beforePageView,
      }),
    // Intentionally only depend on component identity. Inline function props (e.g., landingPageVariant, beforePageView)
    // would otherwise change identity each render and recreate the wrapper, causing remounts and duplicate PAGE_VIEWs.
    [props.component]
  );
  // Exclude custom tracking props from being passed to Route
  const routeProps = omit(props, ['suppressPageView', 'beforePageView', 'loginHeader', 'component']);
  return (
    <Route
      {...routeProps}
      render={(routeChildrenProps) => (
        <AuthenticatedComponent component={component} loginHeader={props.loginHeader} routeProps={routeChildrenProps} />
      )}
    />
  );
};
